import { Box, Button, Checkbox, Paper, Typography, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LearnerDetailsTable } from "./LearnerDetailsTable";
import { LocationState, RowModel } from "AttendanceForm/Types/types";
import { SubmissionFailureModal } from "./SubmissionFailureModal";
import { CheckSubmissionModal } from "./CheckSubmissionModal";
import { LoadingIndicator } from "AttendanceForm/Components/LoadingIndicator/LoadingIndicator";
import { compose } from "redux";
import { withAttendanceFormSessionDetails } from "AttendanceForm/Store/Areas/AttendanceFormEvent/components";
import { useAttendanceFormSessionDetails } from "AttendanceForm/Store/Areas/AttendanceFormEvent/hooks";
import { FormatDateString } from "AttendanceForm/Utils/Dates/dates";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAddLearnerDetailsForm } from "AttendanceForm/Forms/TrainerDetails/useAddLearnerDetailsForm";
import { Form } from "AttendanceForm/Components/FormControls";
import { AttendanceCreationMethod, AttendanceStatus } from "./utils";
import { useLazyQuery } from "@apollo/client/react";
import { GET_ATTENDANCE_RECORDS } from "AttendanceForm/Utils/Queries/LearnerDetails";
import { GetAttendanceRecords_getAttendanceRecords } from "AttendanceForm/Utils/GraphQl/GetAttendanceRecords";
import { useStyles } from "./LearnerDetailsStyles";
import { typographyOverrides } from "AttendanceForm/Theme/Overrides/TypographyOverrides";

export const LearnerDetailsForm = compose<React.FC>(
  withAttendanceFormSessionDetails({ allowRender: true })
)(() => {
  const { t } = useTranslation("learnerDetailsForm");
  const { t: tcommon } = useTranslation("common");
  const styles = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();

  const location = useLocation();
  const { eventId } = useParams<{ eventId?: string }>();
  const searchParams = new URLSearchParams(location.search);
  const eventHash = searchParams.get("sig");
  const {
    session,
    loading: eventValidationLoading,
    fetched,
  } = useAttendanceFormSessionDetails(eventId, eventHash);

  const {
    onSubmit,
    loading: submitLearnerDetailsLoading,
    form,
  } = useAddLearnerDetailsForm({
    initialValues: {
      newAdhocAttendees: [],
      deletedLearnerIds: [],
      updateAttendanceAndNotesByLearnerId: [],
      allAttendees: [],
      attendanceCreationMethod: AttendanceCreationMethod.SUPPLIER,
    },
    sessionDetails: session,
    trainerEmail: (location.state as LocationState).trainerEmail,
    eventHash: eventHash,
  });

  const [hasConfirmedSubmission, setHasConfirmedSubmission] = React.useState(false);
  const [showErrorPopup, setShowErrorPopup] = React.useState(false);
  const [showCheckSubmissionPopup, setShowCheckSubmissionPopup] = React.useState(false);
  const [rows, setRows] = React.useState<RowModel[]>([]);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [popupErrorMessage, setPopupErrorMessage] = React.useState("");

  const [getAttendanceRecords, { loading: getAttendanceRecordsLoading, data: attendanceRecordsData }] =
    useLazyQuery<GetAttendanceRecords_getAttendanceRecords>(GET_ATTENDANCE_RECORDS, {
      fetchPolicy: "no-cache"
    });

  useEffect(() => {
    if (attendanceRecordsData) {
      if (!attendanceRecordsData.attendanceRecords.hasError) {
        const initialRows: RowModel[] = attendanceRecordsData.attendanceRecords.attendanceRecords.map((d) => {
          return {
            id: d.learnerId,
            firstname: d.firstName,
            lastname: d.lastName,
            email: d.email,
            notes: "",
            attended: AttendanceStatus.Blank,
            isExistingAttendanceRecord: true,
          };
        });
        setRows(rows.concat(initialRows));
      } else {
        navigate(`/attendanceform/${eventId}${location.search}`, {
          state: {
            eventId: (location.state as LocationState)?.eventId,
          },
        });
      }
    }
  }, [attendanceRecordsData]);

  useEffect(() => {
    getAttendanceRecords({
      variables: {
        eventId: eventId,
        eventIdHash: eventHash,
      }
    });
  }, [eventId, eventHash, getAttendanceRecords]);

  const onHandleOpenCheckSubmissionPopup = React.useCallback(() => {
    setShowCheckSubmissionPopup(true);
  }, []);

  const onSubmitCallback = React.useCallback(async () => {
    setShowCheckSubmissionPopup(false);
    const checkRowsForMissingFields = rows.map((r) => {
      if (r.firstname === "" || r.lastname === "" || r.email === "" || r.attended === "") {
        return { ...r, hasError: true };
      }
      return { ...r };
    });

    if (checkRowsForMissingFields.some((r) => r.hasError)) {
      setErrorMessage(t("error") + t("validationErrorMessage"));
      setShowErrorPopup(true);
      setRows(checkRowsForMissingFields);
    } else {
      setErrorMessage("");
      const response = await onSubmit();
      if (response.isSuccess && session !== null) {
        navigate("/attendanceform/learnerdetails/submissionsuccess", {
          state: { eventName: session.programmeName, eventDate: session.startDate },
        });
      } else {
        setShowErrorPopup(true);
        setPopupErrorMessage(response.message);
      }
    }
  }, [history, onSubmit, rows, session, t]);

  const onConfirmSubmissionCallback = React.useCallback(
    (e) => {
      if (!rows.some((x) => x.hasError)) setErrorMessage("");
      setHasConfirmedSubmission(e.target.checked);
    },
    [rows]
  );

  const onHandleCloseFailurePopup = React.useCallback(() => {
    setShowErrorPopup(false);
  }, []);

  const onHandleCloseCheckSubmissionPopup = React.useCallback(() => {
    setShowCheckSubmissionPopup(false);
  }, []);

  const checkIfSubmissionIsDisabled = React.useCallback(() => {
    return (
      !hasConfirmedSubmission ||
      !rows.length ||
      rows.some((x) => x.hasError) ||
      rows.some((x) => x.isEditing)
    );
  }, [hasConfirmedSubmission, rows]);

  return (
    <Box>
      {eventValidationLoading ||
        !fetched ||
        submitLearnerDetailsLoading ||
        getAttendanceRecordsLoading ? (
        <Box display="flex" justifyContent="center" className={styles.loader}>
          <LoadingIndicator size="3rem" color="primary" />
        </Box>
      ) : (
        <>
          <Form form={form}>
            <SubmissionFailureModal
              isOpen={showErrorPopup}
              onClose={onHandleCloseFailurePopup}
              eventDate={FormatDateString(session.startDate)}
              eventName={session.programmeName}
              error={popupErrorMessage}
            />
            <CheckSubmissionModal
              isOpen={showCheckSubmissionPopup}
              onClose={onHandleCloseCheckSubmissionPopup}
              onSubmit={onSubmitCallback}
              eventDate={FormatDateString(session.startDate)}
              eventName={session.programmeName}
            />
            <Box className={styles.background} marginBottom={1}>
              <Box height={theme.spacing(7)} marginLeft={1} display={"flex"} alignItems={"center"}>
                <Typography className={styles.title} style={typographyOverrides.subtitle1}>
                  {t("addLearnerDetailsTitle")}
                </Typography>
              </Box>
            </Box>
            <Paper>
              <Box className={styles.learnerTable}>
                <LearnerDetailsTable
                  form={form}
                  rows={rows}
                  setRows={setRows}
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                />
              </Box>
            </Paper>
            <Box className={styles.background} marginBottom={1}>
              <Box height={theme.spacing(7)} marginLeft={1} display={"flex"} alignItems={"center"}>
                <Typography className={styles.title} style={typographyOverrides.subtitle1}>
                  {t("confirmAndSubmit")}
                </Typography>
              </Box>
            </Box>
            <Box
              marginLeft={1}
              marginRight={2}
              paddingBottom={3}
              display="flex"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center">
                <Typography variant="body1">
                  {t("confirmationMessage")}
                  <strong id="event-name-id">
                    {tcommon("eventName")} {"'" + session.programmeName + "'"}
                    {", "}
                  </strong>

                  <strong id="event-date-id">
                    {" "}
                    {tcommon("eventDate")} {FormatDateString(session.startDate)}
                    {", "}
                  </strong>
                  <strong id="eventId-id">
                    {tcommon("eventId")} {"'" + session.externalSessionId + "'"}
                  </strong>
                </Typography>
                <Checkbox
                  disabled={!rows.length}
                  color="primary"
                  onChange={(e) => onConfirmSubmissionCallback(e)}
                />
              </Box>
              <Button
                onClick={onHandleOpenCheckSubmissionPopup}
                variant="contained"
                color={"primary"}
                size="medium"
                disabled={checkIfSubmissionIsDisabled()}
                disableRipple
              >
                {tcommon("submit")}
              </Button>
            </Box>
          </Form>
        </>
      )}
    </Box>
  );
});

